import React from 'react';

export type FdsIconSize = 'xs' | 's' | 'm' | 'l' | 'xl';

export interface FdsIconProps {
  /** Named size token applied to the icon wrapper */
  size?: FdsIconSize;
  /** Fill color applied to the SVG */
  color?: string;
  /** Explicit pixel size; overrides the named size */
  customSize?: number;
  children?: React.ReactNode;
}

const IconComponent: React.FC<FdsIconProps> = ({
  size = 's',
  color,
  customSize,
  children,
}) => (
  <div
    className={`fds-icon fds-icon--${size}`}
    style={{
      fill: color,
      width: customSize ? `${customSize}px` : undefined,
      height: customSize ? `${customSize}px` : undefined,
    }}
  >
    {children}
  </div>
);

// Brief explanation: we're customizing the component by wrapping
// it with another component, and adding customizations to the
// wrapper component. Please refer to `IconComponent` for
// those customizations
export const withFdsIconWrapper = (
  WrappedComponent: React.ComponentType<Record<string, never>>
): React.FC<FdsIconProps> => {
  const name =
    (WrappedComponent as { displayName?: string }).displayName ||
    (WrappedComponent as { name?: string }).name;
  IconComponent.displayName = name;

  const Wrapped: React.FC<FdsIconProps> = (props) => (
    <IconComponent {...props}>
      <WrappedComponent />
    </IconComponent>
  );

  Wrapped.displayName = name;

  return Wrapped;
};

export default withFdsIconWrapper;
